/**
 * Portal Kit: os formulários dos quatro blocos (marca, rodapé, SEO e conteúdo).
 */
import { Link } from 'react-router-dom';

import { Button } from '@/components/atoms';
import {
  TIPOS_NOINDEX,
  type Conteudo,
  type ErroDeCampo,
  type FormatoHora,
  type Marca,
  type ModoRodape,
  type ModoSeo,
  type Rodape,
  type Seo,
  type TipoNoindex,
} from '@/types/portal';

import { CampoCor, CampoInterruptor, CampoNumero, CampoSelecao, CampoTexto } from './campos';
import { EditorLista } from './EditorLista';
import { EditorSelecao } from './EditorSelecao';
import { erroDe, exemploDeDateline, remover, trocar } from './helpers';
import styles from './Portal.module.css';

interface Ctx<C> {
  config: C;
  mudar: (c: C) => void;
  erros: ErroDeCampo[];
}

// ─── marca ───────────────────────────────────────────────────────────────────

export function EditorMarca({ config: c, mudar, erros }: Ctx<Marca>) {
  const cor = (chave: keyof Marca['cores'], rotulo: string) => (
    <CampoCor
      rotulo={rotulo}
      valor={c.cores[chave]}
      erro={erroDe(erros, `config.cores.${chave}`)}
      onChange={(v) => mudar({ ...c, cores: { ...c.cores, [chave]: v } })}
    />
  );
  return (
    <>
      <div className={styles.aviso} role="note">
        <span className={styles.avisoTitulo}>
          Nome, logo e cor primária vêm do cadastro do site
        </span>
        <span>
          {c.nome} · cor {c.cores.primaria} · {c.logo ? 'com logo' : 'sem logo'}. Para mudar, edite
          o site em <Link to="/config/sites">Configurações, Sites WordPress</Link>.
        </span>
      </div>

      <div className={styles.grade}>
        <CampoTexto
          rotulo="Sigla"
          valor={c.sigla}
          mono
          maxLength={12}
          dica="Entra na dateline, por exemplo (EXEMPLO)."
          erro={erroDe(erros, 'config.sigla')}
          onChange={(sigla) => mudar({ ...c, sigla })}
        />
        <CampoTexto
          rotulo="Autor padrão"
          valor={c.autor_padrao}
          maxLength={80}
          dica="Aparece quando a matéria não tem autor, ou o autor é a conta técnica."
          erro={erroDe(erros, 'config.autor_padrao')}
          onChange={(autor_padrao) => mudar({ ...c, autor_padrao })}
        />
        <CampoTexto
          rotulo="Lema"
          valor={c.lema ?? ''}
          maxLength={160}
          erro={erroDe(erros, 'config.lema')}
          onChange={(v) => mudar({ ...c, lema: v === '' ? null : v })}
        />
        <CampoTexto
          rotulo="Edição"
          valor={c.edicao ?? ''}
          maxLength={60}
          erro={erroDe(erros, 'config.edicao')}
          onChange={(v) => mudar({ ...c, edicao: v === '' ? null : v })}
        />
      </div>

      <div>
        <h4 className={styles.sub2}>Cores</h4>
        <div className={styles.grade}>
          {cor('secundaria', 'Secundária')}
          {cor('destaque', 'Destaque')}
          {cor('texto', 'Texto')}
          {cor('fundo', 'Fundo')}
          {cor('positivo', 'Alta (positivo)')}
          {cor('negativo', 'Baixa (negativo)')}
        </div>
      </div>

      <div>
        <h4 className={styles.sub2}>Dateline das matérias</h4>
        <div className={styles.grade}>
          <CampoInterruptor
            rotulo="Mostrar a dateline"
            valor={c.dateline.ativo}
            dica="Montada na hora de exibir, a partir da praça da matéria. Não é gravada no texto."
            onChange={(ativo) => mudar({ ...c, dateline: { ...c.dateline, ativo } })}
          />
          <CampoTexto
            rotulo="Praça padrão"
            valor={c.dateline.praca_padrao}
            maxLength={60}
            dica="Usada quando a matéria não traz praça."
            erro={erroDe(erros, 'config.dateline.praca_padrao')}
            onChange={(praca_padrao) => mudar({ ...c, dateline: { ...c.dateline, praca_padrao } })}
          />
          <CampoTexto
            rotulo="Modelo"
            valor={c.dateline.modelo}
            mono
            maxLength={80}
            dica={`Exemplo: ${exemploDeDateline(c.dateline.modelo, c.dateline.praca_padrao, c.sigla)}`}
            erro={erroDe(erros, 'config.dateline.modelo')}
            onChange={(modelo) => mudar({ ...c, dateline: { ...c.dateline, modelo } })}
          />
        </div>
      </div>
    </>
  );
}

// ─── rodapé ──────────────────────────────────────────────────────────────────

const EXPLICACAO_MODO: Record<ModoRodape, string> = {
  completo: 'Cada bloco mostra a linha de hora, atraso e fonte. Não há selo.',
  selo: 'Os blocos não mostram a linha. O selo aparece uma vez por página.',
  os_dois: 'Cada bloco mostra a linha e o selo aparece uma vez por página.',
};

export function EditorRodape({ config: c, mudar, erros }: Ctx<Rodape>) {
  return (
    <>
      <div className={styles.grade2}>
        <CampoSelecao<ModoRodape>
          rotulo="O que aparece na tela"
          valor={c.modo}
          opcoes={[
            { valor: 'completo', rotulo: 'Linha em cada bloco' },
            { valor: 'selo', rotulo: 'Só o selo' },
            { valor: 'os_dois', rotulo: 'Os dois' },
          ]}
          dica={EXPLICACAO_MODO[c.modo]}
          erro={erroDe(erros, 'config.modo')}
          onChange={(modo) => mudar({ ...c, modo })}
        />
        <p className={styles.dica}>
          A marcação de cada bloco grava sempre hora, atraso e fonte, qualquer que seja o modo. Aqui
          só se decide o que o leitor vê.
        </p>
      </div>

      <div>
        <h4 className={styles.sub2}>Linha de cada bloco</h4>
        <div className={styles.grade}>
          <CampoInterruptor
            rotulo="Mostrar a hora do dado"
            valor={c.hora.exibir}
            onChange={(exibir) => mudar({ ...c, hora: { ...c.hora, exibir } })}
          />
          <CampoTexto
            rotulo="Rótulo da hora"
            valor={c.hora.rotulo}
            maxLength={30}
            erro={erroDe(erros, 'config.hora.rotulo')}
            onChange={(rotulo) => mudar({ ...c, hora: { ...c.hora, rotulo } })}
          />
          <CampoSelecao<FormatoHora>
            rotulo="Formato da hora"
            valor={c.hora.formato}
            opcoes={[
              { valor: 'dia_mes_hora', rotulo: '15/10 às 19:51' },
              { valor: 'hora', rotulo: '19:51' },
            ]}
            onChange={(formato) => mudar({ ...c, hora: { ...c.hora, formato } })}
          />
          <CampoInterruptor
            rotulo="Mostrar o atraso"
            valor={c.atraso.exibir}
            onChange={(exibir) => mudar({ ...c, atraso: { ...c.atraso, exibir } })}
          />
          <CampoTexto
            rotulo="Rótulo do atraso"
            valor={c.atraso.rotulo}
            maxLength={30}
            erro={erroDe(erros, 'config.atraso.rotulo')}
            onChange={(rotulo) => mudar({ ...c, atraso: { ...c.atraso, rotulo } })}
          />
          <CampoNumero
            rotulo="Atraso alto acima de (min)"
            valor={c.atraso.alerta_acima_min}
            min={1}
            max={1440}
            dica="A coluna que declara o seu atraso usa o dela."
            erro={erroDe(erros, 'config.atraso.alerta_acima_min')}
            onChange={(alerta_acima_min) =>
              mudar({ ...c, atraso: { ...c.atraso, alerta_acima_min } })
            }
          />
          <CampoInterruptor
            rotulo="Mostrar a fonte"
            valor={c.fonte.exibir}
            onChange={(exibir) => mudar({ ...c, fonte: { ...c.fonte, exibir } })}
          />
          <CampoTexto
            rotulo="Rótulo da fonte"
            valor={c.fonte.rotulo}
            maxLength={30}
            erro={erroDe(erros, 'config.fonte.rotulo')}
            onChange={(rotulo) => mudar({ ...c, fonte: { ...c.fonte, rotulo } })}
          />
        </div>
      </div>

      <div>
        <h4 className={styles.sub2}>Selo da plataforma (uma vez por página)</h4>
        <div className={styles.grade2}>
          <CampoTexto
            rotulo="Texto do selo"
            valor={c.selo.texto}
            maxLength={80}
            erro={erroDe(erros, 'config.selo.texto')}
            onChange={(texto) => mudar({ ...c, selo: { ...c.selo, texto } })}
          />
          <CampoTexto
            rotulo="Endereço do selo"
            valor={c.selo.url ?? ''}
            tipo="url"
            mono
            placeholder="https://"
            dica="Opcional. Deixe vazio para o selo sem link."
            erro={erroDe(erros, 'config.selo.url')}
            onChange={(v) => mudar({ ...c, selo: { ...c.selo, url: v === '' ? null : v } })}
          />
        </div>
      </div>
    </>
  );
}

// ─── SEO ─────────────────────────────────────────────────────────────────────

const EXPLICACAO_SEO: Record<ModoSeo, string> = {
  auto: 'Se o site tem Yoast, RankMath, AIOSEO ou SEOPress, o plugin só injeta os nós dele no grafo do outro plugin. Sem plugin de SEO, emite os próprios.',
  proprio:
    'O plugin sempre emite canonical, description, Open Graph e JSON-LD das páginas dele. Use quando o site não tem plugin de SEO.',
  desligado: 'O plugin não escreve nada de SEO. Quem emite é o tema ou outro plugin.',
};

const ROTULO_NOINDEX: Record<TipoNoindex, string> = {
  busca: 'Resultados de busca',
  '404': 'Página 404',
  tag: 'Arquivos de tag',
  autor: 'Arquivos de autor',
  data: 'Arquivos por data',
  anexo: 'Páginas de anexo',
  categoria_vazia: 'Categoria sem matérias',
};

interface PrazoProps<I> {
  rotulo: string;
  item: string;
  rotuloChave: string;
  itens: I[];
  chave: (i: I) => string;
  dias: (i: I) => number;
  com: (i: I, parte: { chave?: string; dias?: number }) => I;
  novo: () => I;
  onChange: (itens: I[]) => void;
  erros: ErroDeCampo[];
  campo: string;
  campoChave: string;
}

function EditorPrazos<I>({
  rotulo,
  item,
  rotuloChave,
  itens,
  chave,
  dias,
  com,
  novo,
  onChange,
  erros,
  campo,
  campoChave,
}: PrazoProps<I>) {
  return (
    <div>
      <h4 className={styles.sub2}>
        {rotulo} ({itens.length})
      </h4>
      {itens.length === 0 ? (
        <p className={styles.vazio}>Nenhum.</p>
      ) : (
        <ul className={styles.lista} aria-label={rotulo}>
          {itens.map((it, i) => (
            <li key={i} className={styles.linha}>
              <div className={styles.linhaCampos}>
                <CampoTexto
                  rotulo={`${rotuloChave} ${i + 1}`}
                  valor={chave(it)}
                  mono
                  erro={erroDe(erros, `${campo}.${i}.${campoChave}`)}
                  onChange={(v) => onChange(trocar(itens, i, com(it, { chave: v })))}
                />
                <CampoNumero
                  rotulo={`Dias (${item} ${i + 1})`}
                  valor={dias(it)}
                  min={1}
                  max={365}
                  erro={erroDe(erros, `${campo}.${i}.dias`)}
                  onChange={(v) => onChange(trocar(itens, i, com(it, { dias: v })))}
                />
              </div>
              <div className={styles.linhaAcoes}>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(remover(itens, i))}
                  aria-label={`Remover ${item} ${i + 1}`}
                >
                  Remover
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={itens.length >= 40}
          onClick={() => onChange([...itens, novo()])}
        >
          Adicionar {item}
        </Button>
      </div>
    </div>
  );
}

export function EditorSeo({ config: c, mudar, erros }: Ctx<Seo>) {
  const org = c.organizacao;
  return (
    <>
      <div className={styles.grade2}>
        <CampoSelecao<ModoSeo>
          rotulo="Quem emite o SEO das páginas do plugin"
          valor={c.modo}
          opcoes={[
            { valor: 'auto', rotulo: 'Automático' },
            { valor: 'proprio', rotulo: 'O plugin (sem outro plugin de SEO)' },
            { valor: 'desligado', rotulo: 'Desligado' },
          ]}
          dica={EXPLICACAO_SEO[c.modo]}
          onChange={(modo) => mudar({ ...c, modo })}
        />
        <div style={{ display: 'grid', gap: 'var(--sp-3)', alignContent: 'start' }}>
          <CampoInterruptor
            rotulo="Um único JSON-LD por página"
            valor={c.jsonld_unico}
            dica="A matéria publicada pelo WIREDESK leva o JSON-LD pronto e o plugin emite um só nó, sem duplicar o do tema."
            onChange={(jsonld_unico) => mudar({ ...c, jsonld_unico })}
          />
          <CampoInterruptor
            rotulo="Servir /llms.txt"
            valor={c.llms_txt}
            onChange={(llms_txt) => mudar({ ...c, llms_txt })}
          />
        </div>
      </div>

      <div className={styles.grade}>
        <CampoTexto
          rotulo="Idioma do Open Graph"
          valor={c.idioma_og}
          mono
          placeholder="pt_BR"
          erro={erroDe(erros, 'config.idioma_og')}
          onChange={(idioma_og) => mudar({ ...c, idioma_og })}
        />
        <CampoTexto
          rotulo="Imagem padrão"
          valor={c.imagem_padrao ?? ''}
          tipo="url"
          mono
          placeholder="https://"
          dica="Usada quando a página não tem imagem própria."
          erro={erroDe(erros, 'config.imagem_padrao')}
          onChange={(v) => mudar({ ...c, imagem_padrao: v === '' ? null : v })}
        />
      </div>

      <div>
        <h4 className={styles.sub2}>Organização</h4>
        <div className={styles.grade}>
          <CampoSelecao<Seo['organizacao']['tipo']>
            rotulo="Tipo"
            valor={org.tipo}
            opcoes={[
              { valor: 'NewsMediaOrganization', rotulo: 'Veículo de notícias' },
              { valor: 'Organization', rotulo: 'Organização' },
            ]}
            onChange={(tipo) => mudar({ ...c, organizacao: { ...org, tipo } })}
          />
          <CampoTexto
            rotulo="Página de política editorial (slug)"
            valor={org.politica_editorial_slug ?? ''}
            mono
            erro={erroDe(erros, 'config.organizacao.politica_editorial_slug')}
            onChange={(v) =>
              mudar({ ...c, organizacao: { ...org, politica_editorial_slug: v === '' ? null : v } })
            }
          />
          <CampoTexto
            rotulo="Página de expediente (slug)"
            valor={org.expediente_slug ?? ''}
            mono
            erro={erroDe(erros, 'config.organizacao.expediente_slug')}
            onChange={(v) =>
              mudar({ ...c, organizacao: { ...org, expediente_slug: v === '' ? null : v } })
            }
          />
        </div>
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <EditorLista
            rotulo="Perfis oficiais confirmados"
            item="Perfil"
            dica="Endereços https dos perfis do veículo. Lista vazia é o padrão."
            itens={org.same_as}
            campo="config.organizacao.same_as"
            erros={erros}
            mono
            placeholder="https://"
            max={20}
            onChange={(same_as) => mudar({ ...c, organizacao: { ...org, same_as } })}
          />
        </div>
      </div>

      <EditorLista
        rotulo="Categorias de conteúdo perene"
        item="Categoria"
        dica="Colunas de autoridade: a matéria vira Article. As demais viram NewsArticle."
        itens={c.perenes_categorias}
        campo="config.perenes_categorias"
        erros={erros}
        mono
        max={40}
        onChange={(perenes_categorias) => mudar({ ...c, perenes_categorias })}
      />

      <div>
        <h4 className={styles.sub2}>Conteúdo perecível</h4>
        <p className={styles.dica}>
          Passados os dias, a matéria vira noindex,follow e sai do sitemap (continua no ar).
        </p>
        <div className={styles.grade2}>
          <EditorPrazos
            rotulo="Por categoria"
            item="categoria"
            rotuloChave="Categoria"
            itens={c.pereciveis.categorias}
            chave={(i) => i.categoria}
            dias={(i) => i.dias}
            com={(i, p) => ({ categoria: p.chave ?? i.categoria, dias: p.dias ?? i.dias })}
            novo={() => ({ categoria: '', dias: 2 })}
            campo="config.pereciveis.categorias"
            campoChave="categoria"
            erros={erros}
            onChange={(categorias) => mudar({ ...c, pereciveis: { ...c.pereciveis, categorias } })}
          />
          <EditorPrazos
            rotulo="Por prefixo do endereço"
            item="prefixo"
            rotuloChave="Prefixo"
            itens={c.pereciveis.prefixos_slug}
            chave={(i) => i.prefixo}
            dias={(i) => i.dias}
            com={(i, p) => ({ prefixo: p.chave ?? i.prefixo, dias: p.dias ?? i.dias })}
            novo={() => ({ prefixo: '', dias: 2 })}
            campo="config.pereciveis.prefixos_slug"
            campoChave="prefixo"
            erros={erros}
            onChange={(prefixos_slug) =>
              mudar({ ...c, pereciveis: { ...c.pereciveis, prefixos_slug } })
            }
          />
        </div>
      </div>

      <EditorSelecao<TipoNoindex>
        rotulo="Páginas com noindex"
        dica="Marcadas, saem da busca mas os links delas continuam seguidos."
        opcoes={TIPOS_NOINDEX.map((t) => ({ valor: t, rotulo: ROTULO_NOINDEX[t] }))}
        selecionados={c.noindex}
        onChange={(noindex) => mudar({ ...c, noindex })}
      />
    </>
  );
}

// ─── conteúdo ────────────────────────────────────────────────────────────────

export function EditorConteudo({ config: c, mudar, erros }: Ctx<Conteudo>) {
  const r = c.sem_repeticao;
  const rep = (parcial: Partial<Conteudo['sem_repeticao']>) =>
    mudar({ ...c, sem_repeticao: { ...r, ...parcial } });
  return (
    <>
      <EditorLista
        rotulo="Categorias de notícia"
        item="Categoria"
        dica="Entram na home, no feed e no llms.txt. Pelo slug."
        itens={c.categorias_noticia}
        campo="config.categorias_noticia"
        erros={erros}
        mono
        max={40}
        onChange={(categorias_noticia) => mudar({ ...c, categorias_noticia })}
      />
      <div>
        <h4 className={styles.sub2}>Listas sem repetição</h4>
        <div className={styles.grade}>
          <CampoInterruptor
            rotulo="Evitar foto repetida e matéria quase igual"
            valor={r.ativo}
            onChange={(ativo) => rep({ ativo })}
          />
          <CampoNumero
            rotulo="Janela (h)"
            valor={r.janela_arquivo_h}
            min={1}
            max={720}
            dica="No arquivo e na busca, só conta como repetição dentro desta janela."
            erro={erroDe(erros, 'config.sem_repeticao.janela_arquivo_h')}
            onChange={(janela_arquivo_h) => rep({ janela_arquivo_h })}
          />
          <CampoNumero
            rotulo="Matérias recentes no filtro"
            valor={r.teto_arquivo}
            min={50}
            max={1000}
            erro={erroDe(erros, 'config.sem_repeticao.teto_arquivo')}
            onChange={(teto_arquivo) => rep({ teto_arquivo })}
          />
          <CampoNumero
            rotulo="Termos de título em comum"
            valor={r.termos_mesma_noticia}
            min={2}
            max={10}
            dica="Quantos termos iguais fazem duas matérias serem a mesma notícia."
            erro={erroDe(erros, 'config.sem_repeticao.termos_mesma_noticia')}
            onChange={(termos_mesma_noticia) => rep({ termos_mesma_noticia })}
          />
          <CampoNumero
            rotulo="Distância máxima da foto"
            valor={r.distancia_foto_max}
            min={0}
            max={16}
            dica="Quanto menor, mais parecida a foto precisa ser para contar como a mesma."
            erro={erroDe(erros, 'config.sem_repeticao.distancia_foto_max')}
            onChange={(distancia_foto_max) => rep({ distancia_foto_max })}
          />
          <CampoNumero
            rotulo="Fator de busca"
            valor={r.fator_busca}
            min={1}
            max={10}
            dica="A lista é buscada este número de vezes maior para sobrar matéria depois de pular as repetidas."
            erro={erroDe(erros, 'config.sem_repeticao.fator_busca')}
            onChange={(fator_busca) => rep({ fator_busca })}
          />
        </div>
      </div>
    </>
  );
}
